import type { Book, SourceListItem } from "#/types";

import { getLibrary, getSources } from "#/client";
import { DownloadStatus } from "#/types";
import { useEffect, useState } from "react";

export function isFull(book: Book | undefined): boolean {
  return book?.download_status === DownloadStatus.Full;
}

export type SortMode = "name" | "recent";

export function useLibrary() {
  const [library, setLibrary] = useState<Book[]>([]);
  const [crawlers, setCrawlers] = useState<SourceListItem[]>([]);
  const [nameFilter, setNameFilter] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [sortMode, setSortMode] = useState<SortMode>("recent");
  const [loadingState, setLoadingState] = useState(true);

  const fetchLibrary = async () => {
    try {
      const data = await getLibrary();
      setLibrary(data);
    } catch (error) {
      console.error("Failed to fetch library:", error);
    } finally {
      setLoadingState(false);
    }
  };

  const fetchCrawlers = async () => {
    try {
      const data = await getSources();
      // Map SourceInfo[] to SourceListItem[]
      const crawlers: SourceListItem[] = data
        .filter((s) => !s.disabled)
        .map((s) => ({
          meta: s.config.meta,
        }));
      setCrawlers(crawlers);
    } catch (error) {
      console.error("Failed to fetch crawlers:", error);
    }
  };

  useEffect(() => {
    void fetchLibrary();
    void fetchCrawlers();
  }, []);

  const filteredLibrary = Array.isArray(library)
    ? library
        .filter((book) => {
          const matchesName =
            !nameFilter || book.title.toLowerCase().includes(nameFilter.toLowerCase());
          const matchesSource = !sourceFilter || book.source_id === sourceFilter;
          const matchesStatus = !statusFilter || book.status === statusFilter;
          return matchesName && matchesSource && matchesStatus;
        })
        .toSorted((a, b) => {
          if (sortMode === "name") {
            return a.title.localeCompare(b.title);
          } else {
            // "recent" mode - backend already sorts by updated_at DESC
            // So we keep the original order (which is index order)
            return 0;
          }
        })
    : [];

  return {
    library,
    crawlers,
    nameFilter,
    sourceFilter,
    statusFilter,
    sortMode,
    loadingState,
    filteredLibrary,
    setNameFilter,
    setSourceFilter,
    setStatusFilter,
    setSortMode,
    fetchLibrary,
  };
}
